import type { MonthlyAggregate, ForecastResult, ForecastInput } from "./types.js";

export function movingAverage(values: number[], window: number): number {
  if (values.length === 0) return 0;
  const w = Math.min(window, values.length);
  const slice = values.slice(-w);
  return slice.reduce((sum, v) => sum + v, 0) / slice.length;
}

export function weightedMovingAverage(values: number[], window: number): number {
  if (values.length === 0) return 0;
  const w = Math.min(window, values.length);
  const slice = values.slice(-w);
  let weightSum = 0;
  let total = 0;
  slice.forEach((v, i) => {
    const weight = i + 1; // More recent = higher weight
    total += v * weight;
    weightSum += weight;
  });
  return total / weightSum;
}

export function standardDeviation(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const squaredDiffs = values.map((v) => Math.pow(v - mean, 2));
  const variance = squaredDiffs.reduce((sum, v) => sum + v, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

export function forecastTotal(input: ForecastInput): ForecastResult {
  const { history, monthsAhead } = input;
  if (history.length === 0) {
    return {
      periodStart: "",
      periodEnd: "",
      projected: 0,
      confidenceLow: 0,
      confidenceHigh: 0,
      method: "moving_average",
    };
  }

  const totals = history.map((h) => h.total);
  const projected = weightedMovingAverage(totals, 3);
  const stddev = standardDeviation(totals);

  // Confidence band: ±1.5 standard deviations
  const band = stddev * 1.5 * Math.sqrt(monthsAhead);

  return {
    periodStart: "",
    periodEnd: "",
    projected: Math.round(projected * monthsAhead),
    confidenceLow: Math.max(0, Math.round((projected - band) * monthsAhead)),
    confidenceHigh: Math.round((projected + band) * monthsAhead),
    method: "weighted_average",
  };
}
